import { useState, useEffect } from 'react';
import type { ChatWidgetSettings } from '../types';

interface UseRemoteSettingsOptions {
  settingsEndpoint: string | null;
  settingsApiKey: string | null;
  onError?: (error: Error) => void;
}

interface UseRemoteSettingsResult {
  remoteSettings: ChatWidgetSettings | null;
  isMobile: boolean;
}

export function useRemoteSettings({
  settingsEndpoint,
  settingsApiKey,
  onError,
}: UseRemoteSettingsOptions): UseRemoteSettingsResult {
  const [remoteSettings, setRemoteSettings] = useState<ChatWidgetSettings | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  // Fetch remote settings
  useEffect(() => {
    if (!settingsEndpoint) return;
    let cancelled = false;
    const controller = new AbortController();

    const fetchSettings = async () => {
      try {
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (settingsApiKey) {
          headers.Authorization = `ApiKey ${settingsApiKey}`;
          headers['x-api-key'] = settingsApiKey;
        }
        const res = await fetch(settingsEndpoint, {
          method: 'GET',
          headers,
          signal: controller.signal,
        });
        const payload = await res.json().catch(() => null);
        if (!res.ok) {
          throw new Error(payload?.error || `Failed to load settings (${res.status})`);
        }
        const settingsPayload = payload?.settings ?? payload;
        const resolved = settingsPayload?.settings || settingsPayload;
        if (resolved && typeof resolved === 'object') {
          if (!cancelled) setRemoteSettings(resolved as ChatWidgetSettings);
        } else {
          throw new Error('Settings payload invalid');
        }
      } catch (error) {
        if (cancelled) return;
        if (
          error &&
          typeof error === 'object' &&
          (error as { name?: string }).name === 'AbortError'
        ) {
          return;
        }
        const err = error instanceof Error ? error : new Error('Failed to load settings');
        const isTestEnv = typeof process !== 'undefined' && process.env?.NODE_ENV === 'test';
        // Avoid noisy logs in unit tests; prefer the caller-provided error hook in production.
        if (!isTestEnv && !onError) {
          console.warn('Chat widget settings error:', err);
        }
        onError?.(err);
      }
    };

    fetchSettings();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [settingsEndpoint, settingsApiKey, onError]);

  // Mobile detection
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia(
      '(max-width: 640px), (max-width: 1024px) and (max-height: 640px) and (pointer: coarse)'
    );
    const update = () => setIsMobile(media.matches);
    update();
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', update);
      return () => media.removeEventListener('change', update);
    }
    media.addListener(update);
    return () => media.removeListener(update);
  }, []);

  return { remoteSettings, isMobile };
}
